/**
 * Clear every path that is live in name only, in one go.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Deliberately NOT a BulkActionBar entry: that bar is driven by the checkbox
 * selection and hides itself at zero selected, whereas this acts on every
 * disabled row the tab found — ticking forty boxes first is the chore it
 * exists to remove.
 *
 * Sits INLINE with the Status pills rather than on its own row: it is the verb
 * for the "Disabled" filter beside it, and a full-width row for one action read
 * as a section of its own.
 *
 * Renders nothing when no path is disabled, so a healthy flow never shows a
 * button that would do nothing.
 */

import { FC } from 'react';
import { Hourglass } from 'lucide-react';
import type { QueueElement } from '@trafficpulse/shared';

import { Button } from '@/components/ui';

interface MoveDisabledButtonProps {
  disabledRows: QueueElement[];
  busy: boolean;
  onMove: (rows: QueueElement[]) => void;
}

export const MoveDisabledButton: FC<MoveDisabledButtonProps> = ({
  disabledRows,
  busy,
  onMove,
}) => {
  if (disabledRows.length === 0) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      // Quiet by default: the amber chips already draw the eye, so this only
      // has to be findable once you have decided to act.
      className="h-7 gap-1.5 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
      disabled={busy}
      title={
        `Move all ${disabledRows.length} disabled paths off the tracker and into Waiting. ` +
        'A path counts as disabled when it is switched OFF on the tracker, ' +
        'or when its weight is 0 so it receives no traffic.'
      }
      onClick={() => onMove(disabledRows)}
    >
      <Hourglass className="h-3.5 w-3.5 shrink-0" aria-hidden />
      Move {disabledRows.length} to waiting
    </Button>
  );
};

export default MoveDisabledButton;
